import { useQuery } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import type {
  GetReplenishmentDetailInput,
  GetReplenishmentDetailResponse,
  GetSupplyDemandLinesResponse,
  ReplenishmentDetailBucket,
  ReplenishmentDetailEvent,
  ReplenishmentDetailPlan,
  ReplenishmentEventType,
} from '@erp/rpc';

export type {
  GetReplenishmentDetailResponse,
  ReplenishmentDetailBucket,
  ReplenishmentDetailEvent,
  ReplenishmentDetailPlan,
  ReplenishmentEventType,
};

export interface UseReplenishmentDetailInput {
  /** `im_products_v3.key`. Undefined disables the query. */
  sku: string | undefined;
  /** One stock location. Undefined disables the query. */
  warehouseId: string | undefined;
  mode: GetReplenishmentDetailInput['mode'];
  includeEmptyBuckets: boolean;
  /** Visibility only; PIPE always uses every input. Undefined shows all. */
  eventTypes: ReplenishmentEventType[] | undefined;
}

// One SKU × location's time-phased inputs, display buckets, saved plans and
// PIPE provenance. Changing the display mode or filter keeps the same item's
// last answer on screen; a different item never shows another one's numbers.
export function useReplenishmentDetail(input: UseReplenishmentDetailInput) {
  return useQuery<GetReplenishmentDetailResponse>({
    ...orpc.procurement.replenishmentDetail.get.queryOptions({
      input: {
        // Never sent when absent; `enabled` gates the fetch below.
        sku: input.sku ?? '',
        warehouseId: input.warehouseId ?? '',
        mode: input.mode,
        includeEmptyBuckets: input.includeEmptyBuckets,
        eventTypes: input.eventTypes,
      },
    }),
    enabled: !!input.sku && !!input.warehouseId,
    placeholderData: (previous) =>
      previous?.sku === input.sku && previous?.warehouseId === input.warehouseId
        ? previous
        : undefined,
    staleTime: 60_000,
  });
}

// The order lines behind one SKU × location's On order and opening demand.
// Read only while the lines sheet is open.
export function useSupplyDemandLines(
  input: { sku: string; warehouseId: string },
  enabled: boolean,
) {
  return useQuery<GetSupplyDemandLinesResponse>({
    ...orpc.procurement.replenishmentDetail.getSupplyDemandLines.queryOptions({
      input,
    }),
    enabled,
    staleTime: 60_000,
  });
}
